import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Placeholder from "../../assets/images/placeholder.png";
import viewIcon from "../../assets/images/icons/eye.svg";
import { Link } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../utils/constants";
import editImage from "../../assets/images/icons/edit-2.svg";

const getMediaUrl = (path) => {
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = (process.env.REACT_APP_API || "http://quytech.in:5001").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

function SaudiaHolidayManagement() {
  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10); // Default limit per page
  const [totalPages, setTotalPages] = useState(1);

  const getHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", limit.toString());
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const response = await handleApiCall("get", `${endpoints.holidayPackageList}?${params.toString()}`);
      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const holidaysData = response?.data?.data?.data || response?.data?.data?.holidayPackage || response?.data?.data || [];
        setHolidays(Array.isArray(holidaysData) ? holidaysData : []);
        setTotalPages(response?.data?.data?.pagination?.totalPages || response?.data?.pagination?.totalPages || response?.data?.data?.totalPages || 1);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching holiday packages:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchTerm]);

  useEffect(() => {
    getHolidays();
  }, [getHolidays]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusToggle = async (holidayId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const response = await handleApiCall("patch", `${endpoints.updateHolidayPackageStatus}/${holidayId}`, {
        status: newStatus,
      });

      if (response?.data?.code === 200 || response?.data?.code === "200" || response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success("Holiday package status updated successfully!");
        getHolidays(); // Refresh the list
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error updating holiday package status:", error);
      // Network errors are already handled by handleApiCall
    }
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Saudia Holiday Management</h3>
              <Link to="/add-saudia-holiday" className="btn btn-primary rounded-2 px-3 ml-auto">
                {UI_CONSTANTS.BUTTON_LABELS.CREATE_NEW || "Create New"} <span className="ml-2">+</span>
              </Link>
            </div>

            {/* Search Bar */}
            {/* <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control search-icon left-icon border"
                    placeholder={UI_CONSTANTS.PLACEHOLDERS.SEARCH || "Search..."}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div> */}

            <div className="table-responsive px-0">
              <table className="table table-striped trim">
                <thead>
                  <tr>
                    <th width="80">{UI_CONSTANTS.TABLE_HEADERS.SR_NO || "Sr. No."}</th>
                    <th width="150">{UI_CONSTANTS.TABLE_HEADERS.NAME || "Title"}</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.IMAGE || "Image"}</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.STATUS || "Status"}</th>
                    <th className="text-center">{UI_CONSTANTS.TABLE_HEADERS.ACTIONS || "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}</span>
                        </div>
                      </td>
                    </tr>
                  ) : holidays.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND || "No data found"}
                      </td>
                    </tr>
                  ) : (
                    holidays.map((holiday, index) => (
                      <tr key={holiday._id}>
                        <td>{(currentPage - 1) * limit + index + 1}</td>
                        <td className="text-center">{holiday.title_english || holiday.title || UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND || "N/A"}</td>
                        <td>
                          <div className="sm-img">
                            <img
                              src={getMediaUrl(holiday.image) || Placeholder}
                              alt={UI_CONSTANTS.TABLE_HEADERS.IMAGE || "Image"}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              checked={holiday.status === 1}
                              onChange={() => handleStatusToggle(holiday._id, holiday.status)}
                            />
                            <span className="custom-control-label custom-toggle" />
                          </label>
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center">
                            <Link to={`/view-saudia-holiday/${holiday._id}`} state={{ holiday }} className="btn btn-link p-0 mx-2" title={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"}>
                              <img
                                src={viewIcon}
                                alt={UI_CONSTANTS.BUTTON_LABELS.VIEW || "View"}
                                style={{ width: 20, height: 20, filter: "brightness(0) invert(1)", opacity: 0.95 }}
                                className="mr-2"
                              />
                            </Link>
                            <Link to={`/edit-saudia-holiday/${holiday._id}`} state={{ holiday }} className="btn btn-link p-0 mx-2">
                              <img src={editImage} alt={UI_CONSTANTS.BUTTON_LABELS.EDIT || "Edit"} className="mr-2" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                  <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        {UI_CONSTANTS.BUTTON_LABELS.PREVIOUS || "Previous"}
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                        {UI_CONSTANTS.BUTTON_LABELS.NEXT || "Next"}
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SaudiaHolidayManagement;

